import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IpetState } from "./pet.types";
import axiosInstance from "config/axios";

const initialState: IpetState = {
  pet: [],
  singlePet: {
    _id: "",
    ownerId: "",
    petName: "",
    petType: "",
    petBreed: "",
    petGender: "",
    petWeight: "",
    cancerSuspicion: false,
    birthDate: new Date(),
    age: "",
    photo: "",
    medicalChart: "",
  },
  petError: {},
  isPetActivityInProcess: false,
  isPetDeleteInProcess: false,
  petSuccessMesssage: "",
};

export const getUserPets = createAsyncThunk("getUserPets/pet", async (userId: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().get(`/pet/get-user-pets/${userId}`);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

export const getSinglePet = createAsyncThunk("getSinglePet/pet", async (petID: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().get(`/pet/get-pet/${petID}`);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

export const updateSinglePet = createAsyncThunk("updateSinglePet/pet", async (pet: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().put(`/pet/update-pet/${pet.petId}`, pet.pet);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

export const deleteSinglePet = createAsyncThunk("deleteSinglePet/pet", async (petId: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().delete(`/pet/delete-pet/${petId}`);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

export const addNewPet = createAsyncThunk("addNewPet/pet", async (pet: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().post(`/pet/create-pet`, pet);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

export const addNewPetByAdmin = createAsyncThunk("addNewPetByAdmin/pet", async (pet: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().post(`/pet/create-pet-by-admin`, pet);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

const petSlice = createSlice({
  name: "petSlice",
  initialState: { ...initialState, name: "petInitialState" },
  reducers: {
    errorClean: (state) => {
      state.petError = {
        code: null,
        message: "",
        error: "",
      };
    },
    messageClean: (state) => {
      state.petSuccessMesssage = "";
    },
    singlePetClean: (state) => {
      state.singlePet = {
        _id: "",
        ownerId: "",
        petName: "",
        petType: "",
        petBreed: "",
        petGender: "",
        petWeight: "",
        cancerSuspicion: false,
        // birthYear: "",
        birthDate: "",
        age: "",
        photo: "",
        medicalChart: "",
      };
    },
    petsClean: (state) => {
      state.pet = [];
    },
  },
  extraReducers: (builder) => {
    // get all pets cases
    builder.addCase(getUserPets.pending, (state, action: any) => {
      state.isPetActivityInProcess = true;
    });

    builder.addCase(getUserPets.rejected, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(getUserPets.fulfilled, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petSuccessMesssage = action.payload.message;
      state.pet = action.payload.pets;
    });

    // get single pet by petID cases
    builder.addCase(getSinglePet.pending, (state, action: any) => {
      state.isPetActivityInProcess = true;
    });

    builder.addCase(getSinglePet.rejected, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petError = {
        code: action.payload?.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(getSinglePet.fulfilled, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petSuccessMesssage = action.payload.message;
      state.singlePet = action.payload.pet;
    });

    // update single pet by petID cases
    builder.addCase(updateSinglePet.pending, (state, action: any) => {
      state.isPetActivityInProcess = true;
    });

    builder.addCase(updateSinglePet.rejected, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(updateSinglePet.fulfilled, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petSuccessMesssage = action.payload.message;
      state.singlePet = action.payload.pet;
    });

    // delete single team by teamID cases
    builder.addCase(deleteSinglePet.pending, (state, action: any) => {
      state.isPetDeleteInProcess = true;
    });

    builder.addCase(deleteSinglePet.rejected, (state, action: any) => {
      state.isPetDeleteInProcess = false;
      state.petError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(deleteSinglePet.fulfilled, (state, action: any) => {
      state.isPetDeleteInProcess = false;
      state.petSuccessMesssage = action.payload.message;
      state.singlePet = action.payload.pet;
    });

    // Add New pet cases
    builder.addCase(addNewPet.pending, (state, action: any) => {
      state.isPetActivityInProcess = true;
    });

    builder.addCase(addNewPet.rejected, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });
    builder.addCase(addNewPet.fulfilled, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petSuccessMesssage = action.payload.message;
      state.singlePet = action.payload.pet;
    });

    // add new pet by admin
    builder.addCase(addNewPetByAdmin.pending, (state, action: any) => {
      state.isPetActivityInProcess = true;
    });

    builder.addCase(addNewPetByAdmin.rejected, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(addNewPetByAdmin.fulfilled, (state, action: any) => {
      state.isPetActivityInProcess = false;
      state.petSuccessMesssage = action.payload.message;
      state.singlePet = action.payload.pet;
    });
  },
});
export const { errorClean, messageClean, singlePetClean, petsClean } = petSlice.actions;

export default petSlice.reducer;
